import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { GraduationCap, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_OPTIONS = [
  { slug: "getting_started", label: "Getting Started" },
  { slug: "build_store", label: "Build Your Store" },
  { slug: "get_discovered", label: "Get Discovered" },
  { slug: "manage_business", label: "Manage Your Business" },
  { slug: "grow_business", label: "Grow Your Business" },
];

/**
 * AdminAcademyCard — admin CRUD for tutorial catalog.
 * Lets admins swap YouTube/Vimeo URLs without a code deploy.
 */
export default function AdminAcademyCard() {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    slug: "",
    title: "",
    description: "",
    video_url: "",
    thumbnail_url: "",
    category: "getting_started",
    chapter: 1,
    duration_seconds: "",
    active: true,
    featured: false,
    related_feature: "",
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/academy/admin/tutorials`,
                                { withCredentials: true });
      setTutorials(r.data.tutorials || []);
    } catch {
      toast.error("Couldn't load tutorials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      slug: "", title: "", description: "", video_url: "", thumbnail_url: "",
      category: "getting_started", chapter: 1, duration_seconds: "",
      active: true, featured: false, related_feature: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      slug: t.slug || "",
      title: t.title || "",
      description: t.description || "",
      video_url: t.video_url || "",
      thumbnail_url: t.thumbnail_url || "",
      category: t.category || "getting_started",
      chapter: t.chapter || 1,
      duration_seconds: t.duration_seconds ?? "",
      active: Boolean(t.active),
      featured: Boolean(t.featured),
      related_feature: t.related_feature || "",
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      chapter: parseInt(form.chapter, 10) || 1,
      duration_seconds: form.duration_seconds
        ? parseInt(form.duration_seconds, 10) : null,
      related_feature: form.related_feature || null,
      video_url: form.video_url || null,
      thumbnail_url: form.thumbnail_url || null,
    };
    try {
      if (editing) {
        const { slug, ...patch } = payload; // slug immutable
        await axios.put(
          `${API}/academy/admin/tutorials/${editing.tutorial_id}`, patch,
          { withCredentials: true });
        toast.success("Tutorial updated");
      } else {
        await axios.post(`${API}/academy/admin/tutorials`, payload,
                         { withCredentials: true });
        toast.success("Tutorial created");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await axios.delete(`${API}/academy/admin/tutorials/${t.tutorial_id}`,
                         { withCredentials: true });
      toast.success("Deleted");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card data-testid="admin-academy-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 font-heading">
          <span className="flex items-center gap-2 text-tbc-navy">
            <GraduationCap className="w-5 h-5 text-tbc-gold" />
            Business Academy · Tutorials
          </span>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                size="sm"
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full"
                data-testid="admin-academy-new-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Tutorial
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-tbc-navy">
                  {editing ? "Edit Tutorial" : "New Tutorial"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Slug *</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    placeholder="e.g. add-products"
                    disabled={Boolean(editing)}
                    required
                    data-testid="admin-academy-slug"
                  />
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    required
                    data-testid="admin-academy-title"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Video URL (YouTube or Vimeo)</Label>
                  <Input
                    value={form.video_url}
                    onChange={(e) => setForm((f) => ({ ...f, video_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=…"
                    data-testid="admin-academy-video-url"
                  />
                </div>
                <div>
                  <Label>Thumbnail URL (optional)</Label>
                  <Input
                    value={form.thumbnail_url}
                    onChange={(e) => setForm((f) => ({ ...f, thumbnail_url: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category *</Label>
                    <select
                      value={form.category}
                      onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                      className="w-full h-10 border border-input rounded-md px-3 text-sm bg-transparent"
                      required
                    >
                      {CATEGORY_OPTIONS.map((c) => (
                        <option key={c.slug} value={c.slug}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Chapter</Label>
                    <Input
                      type="number"
                      value={form.chapter}
                      onChange={(e) => setForm((f) => ({ ...f, chapter: e.target.value }))}
                      min={1}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Duration (seconds)</Label>
                    <Input
                      type="number"
                      value={form.duration_seconds}
                      onChange={(e) => setForm((f) => ({ ...f, duration_seconds: e.target.value }))}
                      min={0}
                    />
                  </div>
                  <div>
                    <Label>Related Feature</Label>
                    <Input
                      value={form.related_feature}
                      onChange={(e) => setForm((f) => ({ ...f, related_feature: e.target.value }))}
                      placeholder="blinterest / analytics / orders"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-1">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    />
                    Active
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.featured}
                      onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                    />
                    Featured
                  </label>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full h-11 mt-2"
                  data-testid="admin-academy-submit"
                >
                  {editing ? "Save changes" : "Create tutorial"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : tutorials.length === 0 ? (
          <p className="text-sm text-gray-500">No tutorials yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {tutorials.map((t) => (
              <div
                key={t.tutorial_id}
                className="flex items-center justify-between gap-2 py-2"
                data-testid={`admin-academy-row-${t.slug}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-body text-sm text-tbc-navy truncate">
                    {t.title}
                    {t.featured && <span className="ml-2 text-tbc-gold text-xs">★ featured</span>}
                    {!t.active && <span className="ml-2 text-gray-400 text-xs">· hidden</span>}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {t.category} · Ch{t.chapter} ·{" "}
                    {t.video_url ? "video linked" : "no video yet"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(t)}
                  className="p-1.5 text-gray-500 hover:text-tbc-navy"
                  aria-label="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => del(t)}
                  className="p-1.5 text-gray-500 hover:text-red-500"
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
